import FormGeneralError from 'components/formComponents/FormGeneralError';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setUpdateUsersList } from 'reducers/misc';
import { UsersApi } from '../../api/usersApi';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { IManageUsersListRow } from './models';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ids: number[];
  listRows: IManageUsersListRow[];
};

// What the user is shown once the request has been made
type Outcome =
  | { kind: 'sent'; emailAddresses: string[] }
  | { kind: 'usersUnavailable'; message: string };

export const ResetUserPasswords: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  ids,
  listRows,
}) => {
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const dispatch = useDispatch();
  const isPlural = ids.length > 1;
  const users = listRows
    .filter((row) => ids.includes(row.schoolUsers__Id))
    .map((row) => ({
      id: row.schoolUsers__Id,
      fullName: `${row.contactDetails__FirstName} ${row.contactDetails__LastName}`,
    }));

  const resetPasswords = async () => {
    setError('');
    setIsSubmitting(true);
    const result = await UsersApi.resetUserPassword(ids);
    if (result.data) {
      setOutcome({ kind: 'sent', emailAddresses: result.data.emailAddresses });
    } else if (
      result.error?.formErrors?.some(
        (formError) => formError.messageCode === 'Invalid_SchoolUser'
      )
    ) {
      setOutcome({
        kind: 'usersUnavailable',
        message: result.error.message ?? '',
      });
    } else {
      setError(result.error?.message ?? '');
    }
    setIsSubmitting(false);
  };

  const close = () => {
    togglePanelOpen(false);
    // The list no longer matches the school's users, so load it again
    if (outcome?.kind === 'usersUnavailable') {
      dispatch(setUpdateUsersList(true));
    }
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle={'Reset password' + (isPlural ? 's' : '')}
      position="right"
      primaryBtnTxt={outcome ? 'OK' : 'Confirm'}
      secondaryBtnTxt={outcome ? undefined : 'Cancel'}
      givenClass="ResetUserPasswords"
      handleClose={close}
      handlePrimaryBtn={outcome ? close : resetPasswords}
      handleSecondaryBtn={outcome ? undefined : close}
      width={560}
      isSubmitting={isSubmitting}
    >
      {!outcome && (
        <>
          {error && <FormGeneralError message={error} />}
          {isPlural ? (
            <>
              <p>
                Are you sure that you want to reset the passwords for these{' '}
                {ids.length} users?
              </p>
              <ul>
                {users.map((user) => (
                  <li key={user.id}>{user.fullName}</li>
                ))}
              </ul>
            </>
          ) : (
            <p>
              Are you sure that you want to reset the password for{' '}
              {users[0]?.fullName ?? 'this user'}?
            </p>
          )}
        </>
      )}

      {outcome?.kind === 'usersUnavailable' && <p>{outcome.message}</p>}

      {outcome?.kind === 'sent' && (
        <>
          {outcome.emailAddresses.length > 0 && (
            <>
              <p>The password reset email has been sent to:</p>
              <ul>
                {outcome.emailAddresses.map((emailAddress) => (
                  <li key={emailAddress}>{emailAddress}</li>
                ))}
              </ul>
            </>
          )}
          {outcome.emailAddresses.length < ids.length && (
            <p>Some users could not be emailed, please try again.</p>
          )}
        </>
      )}
    </PopupOrPanel>
  );
};
